import React, { useRef, useState } from 'react';
import { nfConfirm } from './confirm';
import { api } from '../api';
import PhotoLightbox from './PhotoLightbox';

const API_BASE = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
const resolve = (url) => (!url ? null : (url.startsWith('http') ? url : `${API_BASE}${url}`));

/**
 * Фото собранного заказа («фото-контроль»). Добавить (камера/файл), удалить;
 * лимит order.photo_limit (2 на сущность заказа) — считает бэк. Клиентам фото не
 * уходят; у заказов Flowwow последнее отправленное помечено «отправлено покупателю»,
 * пересъёмку отправляет кнопка «Отправить клиенту новое фото» (рендерится снаружи).
 */
export default function OrderPhotos({ order, showToast, onUpdated, compact = false, readOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [viewAt, setViewAt] = useState(null);   // индекс фото в просмотре (поверх карточки, не в новой вкладке)
  const inputRef = useRef(null);
  const photos = order.photos || [];
  const limit = order.photo_limit || 2;
  const closed = ['completed', 'cancelled'].includes(order.status);
  const canAdd = !readOnly && !closed && photos.length < limit;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { showToast('Нужен JPEG или PNG', 'error'); return; }
    setBusy(true);
    try {
      await api.addOrderPhoto(order.id, file);
      if (onUpdated) await onUpdated();
    } catch (error) {
      showToast(error.message || 'Не удалось загрузить фото', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (photo) => {
    if (!(await nfConfirm('Удалить фото?', { confirmText: 'Удалить' }))) return;
    setBusy(true);
    try {
      await api.deleteOrderPhoto(order.id, photo.id);
      if (onUpdated) await onUpdated();
    } catch (error) {
      showToast(error.message || 'Не удалось удалить фото', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {photos.map((ph, i) => (
        <div key={ph.id} style={{ position: 'relative' }}>
          <button type="button" onClick={() => setViewAt(i)} aria-label="Открыть фото"
            style={{ padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', display: 'block' }}>
            <img src={resolve(ph.url)} alt="Фото заказа"
              style={{ width: compact ? 48 : 64, height: compact ? 48 : 64, objectFit: 'cover', '--ch': '6px', '--stroke': (ph.sent_at ? '#C2185B' : '#EBEDEC'), '--bw': '2px', border: ph.sent_at ? '2px solid #C2185B' : '1px solid #EBEDEC', display: 'block' }}
              title={ph.sent_at ? `Отправлено покупателю ${new Date(ph.sent_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : new Date(ph.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} />
          </button>
          {!readOnly && !closed && (
            <button type="button" onClick={() => handleDelete(ph)} disabled={busy} title="Удалить фото"
              style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, '--ch': '8px', border: 'none', background: '#CB4D45', color: '#fff', fontSize: 11, lineHeight: '18px', padding: 0, cursor: 'pointer' }}>×</button>
          )}
        </div>
      ))}
      {canAdd && (
        <>
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? 'Загрузка...' : (photos.length ? '+ Ещё фото' : 'Добавить фото заказа')}
          </button>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png" capture="environment" onChange={handleFile} style={{ display: 'none' }} />
        </>
      )}
      <span style={{ fontSize: 11, color: '#999' }}>{photos.length} из {limit}</span>
      {viewAt != null && photos.length > 0 && (
        <PhotoLightbox images={photos.map(ph => ({ src: resolve(ph.url) }))} startIndex={Math.min(viewAt, photos.length - 1)}
          onClose={() => setViewAt(null)} />
      )}
    </div>
  );
}
